import { useEffect } from "react";
import { useForm } from "react-hook-form";
import FormModal from "@/components/FormModal/FormModal";
import formRenderer from "@/lib/formRenderer";
import type { FormConfig } from "@/lib/formRenderer";
import PlusIcon from "@/components/SVGComponents/PlusIcon";
import { combineClasses, notBlankValidator, requiredMessage } from "@/lib/utils";
import { numberRegex, floatingPointNumberRegex, noLeadingSpaceRegex } from "@/lib/regex";
import toast from "@/lib/toast";
import subscriptionService from "@/services/api/subscription";
import type { SubscriptionPlan } from "@/services/api/subscription";
import { USER_CATEGORY_OPTIONS } from "@/services/api/user";
import type { UserCategory } from "@/services/api/user";
import styles from "./CreatePlanModal.module.scss";

// Each limit is used twice: as the validation rule (the error message) and
// as the input's native `maxLength`/`max` (so typing stops at the limit).
const PLAN_NAME_MAX_LENGTH = 50;
const TAG_MAX_LENGTH = 20; // Shown as PlanCard's badge — kept short.
const DESCRIPTION_MAX_LENGTH = 1000;
const PLAN_DURATION_MAX_LENGTH = 2;
const MAX_PLAN_DURATION_MONTHS = 99;
const MAX_PRICE = 10000000; // ₹1 Cr.
// "10000000" plus up to 3 decimals (what `floatingPointNumberRegex` allows).
const PRICE_MAX_LENGTH = String(MAX_PRICE).length + 4;

const GRID_FIELDS: FormConfig = {
  planName: {
    controlType: "input",
    label: "Plan name",
    labelVariant: "caption",
    placeholder: "e.g. Patron",
    isRequired: true,
    validations: {
      required: requiredMessage("Plan name"),
      pattern: { value: noLeadingSpaceRegex, message: "Plan name can't start with a space" },
      maxLength: {
        value: PLAN_NAME_MAX_LENGTH,
        message: `Plan name must be at most ${PLAN_NAME_MAX_LENGTH} characters`,
      },
    },
    props: { maxLength: PLAN_NAME_MAX_LENGTH, className: combineClasses(styles.fieldInput) },
  },
  // Maps directly to the real `planDurationMonth` field (services/api/
  // subscription.ts) — PlanCard.tsx already renders it as "every N
  // months", so this collects the same number rather than a guessed
  // monthly/annual billing-cycle concept that isn't part of the real API.
  planDurationMonth: {
    controlType: "input",
    label: "Plan duration (months)",
    labelVariant: "caption",
    placeholder: "e.g. 12",
    isRequired: true,
    validations: {
      required: requiredMessage("Plan duration"),
      pattern: { value: numberRegex, message: "Plan duration must be a whole number" },
      min: { value: 1, message: "Plan duration must be at least 1 month" },
      max: {
        value: MAX_PLAN_DURATION_MONTHS,
        message: `Plan duration can't exceed ${MAX_PLAN_DURATION_MONTHS} months`,
      },
      maxLength: {
        value: PLAN_DURATION_MAX_LENGTH,
        message: `Plan duration must be at most ${PLAN_DURATION_MAX_LENGTH} digits`,
      },
    },
    // `type: "text"` + numeric `inputMode` rather than `type: "number"` —
    // see AddVenueModal's `rateField` for why (maxLength + overflow).
    props: {
      type: "text",
      inputMode: "numeric",
      maxLength: PLAN_DURATION_MAX_LENGTH,
      className: combineClasses(styles.fieldInput),
      onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (
          !/^\d$/.test(e.key) &&
          !["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "Enter"].includes(e.key) &&
          !e.ctrlKey &&
          !e.metaKey
        ) {
          e.preventDefault();
        }
      },
    },
  },
  price: {
    controlType: "input",
    label: "Price (excl. GST)",
    labelVariant: "caption",
    isRequired: true,
    validations: {
      required: requiredMessage("Price"),
      pattern: { value: floatingPointNumberRegex, message: "Enter a valid amount" },
      min: { value: 0, message: "Price can't be negative" },
      max: { value: MAX_PRICE, message: `Price can't exceed ₹${MAX_PRICE.toLocaleString("en-IN")}` },
    },
    props: {
      type: "text",
      inputMode: "decimal",
      maxLength: PRICE_MAX_LENGTH,
      startIcon: <span aria-hidden="true">₹</span>,
      className: combineClasses(styles.fieldInput),
    },
  },
  // Real `tag` field — a short label shown as PlanCard's badge (e.g.
  // "PATRON"), not the numeric seat count this field previously
  // (mis)collected before this modal was wired to a real endpoint.
  tag: {
    controlType: "input",
    label: "Tag",
    labelVariant: "caption",
    placeholder: "e.g. Patron",
    isRequired: true,
    validations: {
      required: requiredMessage("Tag"),
      validate: notBlankValidator("Tag"),
      maxLength: { value: TAG_MAX_LENGTH, message: `Tag must be at most ${TAG_MAX_LENGTH} characters` },
    },
    props: { maxLength: TAG_MAX_LENGTH, className: combineClasses(styles.fieldInput) },
  },
  // Same Chartered/Unchartered select as AddMemberModal's Category —
  // sent as `userCategory` on create/update.
  userCategory: {
    controlType: "select",
    label: "User category",
    labelVariant: "caption",
    isControlled: true,
    isRequired: true,
    validations: { required: requiredMessage("User category") },
    props: { options: USER_CATEGORY_OPTIONS, isSearchable: false, className: combineClasses(styles.fieldSelect) },
  },
};

const INCLUDES_FIELD: FormConfig = {
  description: {
    controlType: "textarea",
    label: "What this plan includes",
    labelVariant: "caption",
    placeholder: "One benefit per line — shown on the membership screen in the app.",
    isRequired: true,
    validations: {
      required: requiredMessage("Description"),
      validate: notBlankValidator("Description"),
      maxLength: {
        value: DESCRIPTION_MAX_LENGTH,
        message: `Must be at most ${DESCRIPTION_MAX_LENGTH} characters`,
      },
    },
    props: { maxLength: DESCRIPTION_MAX_LENGTH },
  },
};

interface CreatePlanFormValues {
  planName: string;
  planDurationMonth: string;
  price: string;
  tag: string;
  description: string;
  userCategory: UserCategoryOption | null;
}

type UserCategoryOption = (typeof USER_CATEGORY_OPTIONS)[number];

const DEFAULT_USER_CATEGORY =  null;

// Prefills the edit form from the plan's own `userCategory` when the API
// returns one; falls back to the same default as create.
const getUserCategoryOption = (category: UserCategory | null | undefined) =>
  USER_CATEGORY_OPTIONS.find((option) => option.value === category) ?? DEFAULT_USER_CATEGORY;

export interface CreatePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Present → "Edit plan" (prefilled from this plan, calls
  // subscriptionService.updateSubscriptionPlan). Absent/null → "Create a
  // plan" (create) — same convention as AddVenueModal's `venueToEdit`
  // (pages/CitiesChapters/components/AddVenueModal/AddVenueModal.tsx).
  planToEdit?: SubscriptionPlan | null;
  // Called after a successful create/update so the caller can refresh its
  // plan list — same convention as AddVenueModal's `onSaved`.
  onSaved: () => void;
}

const CreatePlanModal = ({ isOpen, onClose, planToEdit = null, onSaved }: CreatePlanModalProps) => {
  const isEditing = planToEdit !== null;
  const { register, handleSubmit, control, reset, formState } = useForm<CreatePlanFormValues>({
    defaultValues: {
      planName: "",
      planDurationMonth: "",
      price: "",
      tag: "",
      description: "",
      userCategory: DEFAULT_USER_CATEGORY,
    },
    mode: "onTouched",
  });

  // This same modal instance is reused for both "Create plan" and every
  // plan's "Edit plan" click (Subscriptions.tsx just swaps which
  // `planToEdit` it passes) — the form has to be explicitly reset each
  // time it opens, since useForm's own `defaultValues` are only read once
  // at mount. Same pattern as AddVenueModal's own reset effect.
  useEffect(() => {
    if (!isOpen) return;

    if (!planToEdit) {
      reset({
        planName: "",
        planDurationMonth: "",
        price: "",
        tag: "",
        description: "",
        userCategory: DEFAULT_USER_CATEGORY,
      });
      return;
    }

    reset({
      planName: planToEdit.name,
      planDurationMonth: String(planToEdit.planDurationMonth),
      price: String(planToEdit.amount),
      tag: planToEdit.tag,
      description: planToEdit.description,
      userCategory: getUserCategoryOption(planToEdit.userCategory),
    });
  }, [isOpen, planToEdit, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    if (!values.userCategory) return;
    const userCategory = values.userCategory.value;
    try {
      if (isEditing && planToEdit) {
        await subscriptionService.updateSubscriptionPlan({
          id: planToEdit.id,
          name: values.planName.trim(),
          amount: Number(values.price) || 0,
          planDurationMonth: Number(values.planDurationMonth) || 0,
          description: values.description.trim(),
          tag: values.tag.trim(),
          userCategory,
        });
        toast.success("Plan updated.");
      } else {
        await subscriptionService.createSubscriptionPlan({
          name: values.planName.trim(),
          amount: values.price,
          planDurationMonth: Number(values.planDurationMonth) || 0,
          description: values.description.trim(),
          tag: values.tag.trim(),
          userCategory,
        });
        toast.success("Plan created.");
      }
      onSaved();
      handleClose();
    } catch {
      // privateRequest's response interceptor already shows an error toast
      // (see services/axios.ts) — nothing more to do here.
    }
  });

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? "Edit plan" : "Create a plan"}
      subtitle="Plans appear at checkout and on the membership record."
      onSubmit={onSubmit}
      submitLabel={isEditing ? "Save changes" : "Create plan"}
      submitIcon={<PlusIcon />}
      submitDisabled={formState.isSubmitting || !formState.isValid}
      className={combineClasses(styles.createPlanModal)}
    >
      <div className={styles.fieldGrid}>{formRenderer<CreatePlanFormValues>(GRID_FIELDS, formState, register, control)}</div>

      {formRenderer<CreatePlanFormValues>(INCLUDES_FIELD, formState, register, control)}
    </FormModal>
  );
};

export default CreatePlanModal;
